import { useEffect, useState } from 'react'
import { AmountInput, Button, DatePicker, Drawer, SearchSelect } from '../../components'
import { useAccounts } from '../../lib/api/accounts'
import { useCategories } from '../../lib/api/categories'
import { useCounterparties } from '../../lib/api/counterparties'
import { useOrganizations } from '../../lib/api/organizations'
import { useRecurring } from '../../lib/api/recurring'
import {
  useCreatePlanned,
  useUpdatePlanned,
} from '../../lib/api/planning'
import type { PlannedTransaction } from '../../lib/api/planning'
import { Field } from './rule-builder'

type Props = {
  item: PlannedTransaction | null // null → create mode
  onClose: () => void
}

export function PlannedDrawer({ item, onClose }: Props) {
  const orgs = useOrganizations()
  const counterparties = useCounterparties()
  const recurring = useRecurring()
  const create = useCreatePlanned()
  const update = useUpdatePlanned()
  const [error, setError] = useState<string | null>(null)

  const defaultOrgId = (orgs.data ?? [])[0]?.id ?? ''

  const [organizationId, setOrganizationId] = useState(item?.organizationId ?? defaultOrgId)
  const [type, setType] = useState<'income' | 'expense' | 'transfer'>(item?.type ?? 'expense')
  const [plannedDate, setPlannedDate] = useState(
    item?.plannedDate ?? new Date().toISOString().slice(0, 10),
  )
  const [accountId, setAccountId] = useState(item?.accountId ?? '')
  const [toAccountId, setToAccountId] = useState(item?.toAccountId ?? '')
  const [categoryId, setCategoryId] = useState(item?.categoryId ?? '')
  const [counterpartyId, setCounterpartyId] = useState(item?.counterpartyId ?? '')
  const [amount, setAmount] = useState(String(item?.amount ?? ''))
  const [currency, setCurrency] = useState<'UAH' | 'USD' | 'EUR'>(item?.currency ?? 'UAH')
  const [description, setDescription] = useState(item?.description ?? '')
  const [recurringTemplateId, setRecurringTemplateId] = useState(item?.recurringTemplateId ?? '')

  const accounts = useAccounts({ organizationId: organizationId || null })
  const allCategories = useCategories()

  // Reset accountId when org changes
  useEffect(() => {
    if (!item) setAccountId('')
  }, [organizationId, item])

  // Auto-select first account
  useEffect(() => {
    if (!accountId && accounts.data && accounts.data.length > 0) {
      setAccountId(accounts.data[0].id)
    }
  }, [accountId, accounts.data])

  // Auto-select first category
  useEffect(() => {
    if (!categoryId && allCategories.data && allCategories.data.length > 0) {
      setCategoryId(allCategories.data[0].id)
    }
  }, [categoryId, allCategories.data])

  const submit = async () => {
    setError(null)
    if (!organizationId || !accountId || !categoryId || !amount || !plannedDate) {
      setError('Заповніть усі обовʼязкові поля')
      return
    }
    try {
      const payload = {
        organizationId,
        type,
        plannedDate,
        accountId,
        categoryId,
        amount: Number(amount),
        currency,
        ...(toAccountId && type === 'transfer' ? { toAccountId } : {}),
        ...(counterpartyId ? { counterpartyId } : {}),
        ...(description ? { description } : {}),
        ...(recurringTemplateId ? { recurringTemplateId } : {}),
      }
      if (item) {
        await update.mutateAsync({ id: item.id, ...payload })
      } else {
        await create.mutateAsync(payload as Parameters<typeof create.mutateAsync>[0])
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Невідома помилка')
    }
  }

  const isPending = create.isPending || update.isPending
  const categoryOptions = (allCategories.data ?? [])
  const accountOptions = accounts.data ?? []
  const orgOptions = orgs.data ?? []
  const counterpartyOptions = counterparties.data ?? []
  const recurringOptions = (recurring.data?.items ?? []).filter((r) => r.isActive)

  return (
    <Drawer
      onClose={onClose}
      title={item ? 'Редагувати планову операцію' : 'Нова планова операція'}
      width={520}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 16 }}>
        <Field label="Організація *">
          <SearchSelect
            className="input"
            value={organizationId}
            onChange={setOrganizationId}
            placeholder="Оберіть організацію…"
            options={orgOptions.map((o) => ({ id: o.id, label: o.name }))}
          />
        </Field>

        <Field label="Тип *">
          <div style={{ display: 'flex', gap: 8 }}>
            {(['income', 'expense', 'transfer'] as const).map((t) => (
              <label
                key={t}
                style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}
              >
                <input
                  type="radio"
                  name="type"
                  value={t}
                  checked={type === t}
                  onChange={() => setType(t)}
                />
                {t === 'income' ? 'Прихід' : t === 'expense' ? 'Витрата' : 'Переказ'}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Дата *">
          <DatePicker className="input" value={plannedDate} onChange={setPlannedDate} />
        </Field>

        <Field label="Рахунок *">
          <SearchSelect
            className="input"
            value={accountId}
            onChange={setAccountId}
            placeholder="Оберіть рахунок…"
            options={accountOptions.map((a) => ({ id: a.id, label: a.name, hint: a.currency }))}
          />
        </Field>

        {type === 'transfer' && (
          <Field label="Рахунок отримувача">
            <SearchSelect
              className="input"
              value={toAccountId}
              onChange={setToAccountId}
              emptyOption
              emptyLabel="Оберіть рахунок…"
              placeholder="Оберіть рахунок…"
              options={accountOptions.map((a) => ({ id: a.id, label: a.name, hint: a.currency }))}
            />
          </Field>
        )}

        <Field label="Категорія *">
          <SearchSelect
            className="input"
            value={categoryId}
            onChange={setCategoryId}
            placeholder="Оберіть категорію…"
            options={categoryOptions.map((c) => ({ id: c.id, label: c.name }))}
          />
        </Field>

        <Field label="Контрагент">
          <SearchSelect
            className="input"
            value={counterpartyId}
            onChange={setCounterpartyId}
            emptyOption
            emptyLabel="Без контрагента"
            placeholder="Без контрагента"
            options={counterpartyOptions.map((c) => ({ id: c.id, label: c.name }))}
          />
        </Field>

        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <Field label="Сума *">
              <AmountInput
                value={amount}
                onChange={setAmount}
                decimals={2}
                className="input num"
              />
            </Field>
          </div>
          <div style={{ width: 100 }}>
            <Field label="Валюта *">
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value as 'UAH' | 'USD' | 'EUR')}
                className="input"
              >
                <option value="UAH">UAH</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </Field>
          </div>
        </div>

        <Field label="Опис">
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Коментар…"
            className="input"
          />
        </Field>

        {recurringOptions.length > 0 && (
          <Field label="Регулярний шаблон">
            <SearchSelect
              className="input"
              value={recurringTemplateId}
              onChange={setRecurringTemplateId}
              emptyOption
              emptyLabel="Без шаблону"
              placeholder="Без шаблону"
              options={recurringOptions.map((r) => ({ id: r.id, label: r.name }))}
            />
          </Field>
        )}

        {error && <div style={{ fontSize: 13, color: 'var(--danger-fg)' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Скасувати
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={submit}
            disabled={isPending}
          >
            {isPending ? 'Збереження…' : item ? 'Зберегти' : 'Створити'}
          </Button>
        </div>
      </div>
    </Drawer>
  )
}
